import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export interface AnchoredMenuPosition {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
}

interface UseAnchoredPortalMenuOptions {
  isOpen: boolean;
  /** Preferred max menu height in px. */
  preferredMaxHeight?: number;
  /** Open upward when space below is less than this (px). */
  minSpaceBelow?: number;
  /** Minimum menu width in px. */
  minWidth?: number;
}

/**
 * Positions a portaled menu with fixed coordinates from an anchor control.
 * Avoids clipping by overflow:hidden ancestors and stacking under sibling cards.
 */
export function useAnchoredPortalMenu({
  isOpen,
  preferredMaxHeight = 280,
  minSpaceBelow = 180,
  minWidth = 220,
}: UseAnchoredPortalMenuOptions) {
  const controlRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<AnchoredMenuPosition | null>(null);

  const updateMenuPosition = useCallback(() => {
    const control = controlRef.current;
    if (!control) return;

    const rect = control.getBoundingClientRect();
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;
    const openUpward = spaceBelow < minSpaceBelow && spaceAbove > spaceBelow;
    const available = Math.max(140, openUpward ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(preferredMaxHeight, available);
    const width = Math.max(rect.width, minWidth);
    const left = Math.min(
      Math.max(viewportPadding, rect.left),
      Math.max(viewportPadding, window.innerWidth - width - viewportPadding)
    );
    const top = openUpward
      ? Math.max(viewportPadding, rect.top - maxHeight - 4)
      : rect.bottom + 4;

    setMenuPosition({ top, left, width, maxHeight });
  }, [minSpaceBelow, minWidth, preferredMaxHeight]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return;
    }

    updateMenuPosition();

    const handleReposition = () => updateMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updateMenuPosition]);

  return { controlRef, menuRef, menuPosition, updateMenuPosition };
}
